import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { BadgeCheck, ShieldCheck, Truck, RotateCcw, ArrowLeft, Loader2, ChevronDown, Gift, Info, ShoppingBag, Minus, Plus, Zap } from "lucide-react";
import { api } from "../lib/api";
import AffirmPromo from "../components/AffirmPromo";
import ApplyButton from "../components/ApplyButton";
import TrustMarquee from "../components/TrustMarquee";
import CarrierTag from "../components/CarrierTag";
import { weeklyLease } from "../lib/lease";
import { isIpad, isMacbook } from "../lib/category";
import { useCart } from "../context/CartContext";
import { trackViewContent, trackAddToCart } from "../lib/metaPixel";

const specs = [
  { icon: BadgeCheck, label: "PhoneCheck Certified 100% functional" },
  { icon: ShieldCheck, label: "1-Year replacement warranty" },
  { icon: Truck, label: "Free UPS shipping" },
  { icon: RotateCcw, label: "30-Day returns" },
];

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [status, setStatus] = useState("loading");
  const [selected, setSelected] = useState(null);
  const [activeImage, setActiveImage] = useState("");
  const [qty, setQty] = useState(1);
  const { addItem } = useCart();

  useEffect(() => {
    api.get(`/products/${id}`)
      .then((r) => {
        setProduct(r.data);
        const vs = r.data.variants || [];
        const initial = vs.find((v) => v.in_stock) || vs[0] || null;
        setSelected(initial);
        setActiveImage((initial && initial.image) || (r.data.images && r.data.images[0]) || r.data.image_url || "");
        setStatus("ok");
        trackViewContent({ id: r.data.id, name: r.data.name, price: (initial && initial.price) || r.data.price });
      })
      .catch(() => setStatus("notfound"));
  }, [id]);

  const handleSelectVariant = (v) => {
    setSelected(v);
    setQty(1);
    if (v && v.image) setActiveImage(v.image);
  };

  if (status === "loading") {
    return (
      <div className="flex min-h-screen items-center justify-center" data-testid="product-detail-loading">
        <Loader2 className="h-10 w-10 animate-spin text-crimson" />
      </div>
    );
  }

  if (status === "notfound") {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4" data-testid="product-detail-notfound">
        <p className="font-display text-2xl font-bold text-ink">This iPhone isn't available.</p>
        <Link to="/products" className="rounded-full bg-crimson px-6 py-3 font-bold text-white">Back to catalog</Link>
      </div>
    );
  }

  const variants = product.variants || [];
  const gallery = (product.images && product.images.length ? product.images : [product.image_url]).filter(Boolean);
  const activePrice = selected ? selected.price : product.price;
  const activeWeekly = weeklyLease(activePrice);
  const activeInStock = selected ? selected.in_stock : product.in_stock;
  const activeSku = selected ? selected.sku : product.sku;
  const activeMax = selected ? selected.quantity : product.stock_quantity; // null = untracked/unlimited
  const atMax = activeMax != null && qty >= activeMax;

  const CONDITION_ORDER = ["Like New", "Excellent", "Good"];
  const conditions = CONDITION_ORDER.filter((c) => variants.some((v) => v.condition === c));
  const hasConditions = conditions.length > 0;
  const activeCondition = selected?.condition || "";
  const visibleVariants = hasConditions ? variants.filter((v) => v.condition === activeCondition) : variants;
  // Hide sold-out options for a cleaner dropdown; fall back to all if none are in stock.
  const inStockVisible = visibleVariants.filter((v) => v.in_stock);
  const optionVariants = inStockVisible.length ? inStockVisible : visibleVariants;
  const conditionSoldOut = (c) => {
    const pool = variants.filter((v) => v.condition === c);
    return pool.length > 0 && pool.every((v) => !v.in_stock);
  };

  const selectCondition = (c) => {
    const pool = variants.filter((v) => v.condition === c);
    const match =
      pool.find((v) => v.color === selected?.color && v.storage === selected?.storage && v.in_stock) ||
      pool.find((v) => v.in_stock) ||
      pool.find((v) => v.color === selected?.color && v.storage === selected?.storage) ||
      pool[0];
    if (match) handleSelectVariant(match);
  };

  const addToCart = () => {
    if (!activeInStock) return;
    addItem({
      product_id: product.id,
      variant_id: selected?.id || null,
      name: product.name,
      sku: activeSku || "",
      price: activePrice,
      image: activeImage || product.image_url || "",
      color: selected?.color || product.color || "",
      storage: selected?.storage || product.storage || "",
      condition: selected?.condition || "",
      max_qty: activeMax ?? null,
    }, qty);
    trackAddToCart({ id: product.id, name: product.name, price: activePrice, quantity: qty });
    setQty(1);
  };

  return (
    <div className="pt-28" data-testid="product-detail-page">
      <div className="mx-auto max-w-7xl px-6 lg:px-12">
        <button onClick={() => navigate(-1)} data-testid="detail-back" className="mb-8 inline-flex items-center gap-2 text-sm font-bold text-muted-foreground hover:text-crimson">
          <ArrowLeft className="h-4 w-4" /> Back
        </button>

        <div className="grid gap-12 lg:grid-cols-2">
          <div>
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              className="relative overflow-hidden rounded-3xl border border-line bg-alt"
            >
              <div className="absolute inset-0 -z-0 mx-auto my-auto h-64 w-64 rounded-full bg-crimson/10 blur-3xl" style={{ left: "50%", top: "50%", transform: "translate(-50%,-50%)" }} />
              <img src={activeImage} alt={product.name} className="relative z-10 aspect-square w-full object-contain p-6" data-testid="detail-image" />
            </motion.div>
            {gallery.length > 1 && (
              <div className="mt-4 flex flex-wrap gap-3" data-testid="detail-gallery">
                {gallery.map((src, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveImage(src)}
                    data-testid={`detail-thumb-${i}`}
                    className={`h-16 w-16 overflow-hidden rounded-xl border-2 bg-alt transition-colors ${activeImage === src ? "border-crimson" : "border-line hover:border-ink"}`}
                  >
                    <img src={src} alt={`${product.name} view ${i + 1}`} className="h-full w-full object-contain p-1" />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div>
            <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-emerald-600">
              <BadgeCheck className="h-4 w-4" /> {product.condition}
            </div>
            <h1 className="mt-3 font-display text-4xl font-black tracking-tighter text-ink sm:text-5xl" data-testid="detail-name">{product.name}</h1>
            <p className="mt-2 text-lg text-muted-foreground">
              {[product.storage, product.color].filter(Boolean).join(" · ")}
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <span
                data-testid="detail-stock-badge"
                className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-bold ${activeInStock ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-crimson"}`}
              >
                <span className={`h-1.5 w-1.5 rounded-full ${activeInStock ? "bg-emerald-500" : "bg-crimson"}`} />
                {activeInStock ? "In stock" : "Sold out"}
              </span>
              <CarrierTag carrier={product.carrier} />
            </div>

            {hasConditions && (
              <div className="mt-8" data-testid="condition-selector">
                <div className="flex items-center gap-2">
                  <label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">1. Choose condition</label>
                  <a
                    href="/conditions"
                    target="_blank"
                    rel="noreferrer"
                    data-testid="condition-info-link"
                    aria-label="Learn what each condition means"
                    className="inline-flex items-center text-muted-foreground transition-colors hover:text-crimson"
                  >
                    <Info className="h-4 w-4" />
                  </a>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {conditions.map((c) => {
                    const soldOut = conditionSoldOut(c);
                    return (
                      <button
                        key={c}
                        onClick={() => selectCondition(c)}
                        data-testid={`condition-${c.replace(/\s+/g, "-").toLowerCase()}`}
                        aria-label={soldOut ? `${c} — sold out` : c}
                        className={`inline-flex items-center gap-1.5 rounded-xl border px-4 py-2.5 text-sm font-bold transition-colors ${activeCondition === c ? "border-crimson bg-crimson/5 text-crimson" : soldOut ? "border-line text-muted-foreground hover:border-ink" : "border-line text-ink hover:border-ink"}`}
                      >
                        <span className={soldOut ? "line-through decoration-crimson decoration-2" : ""}>{c}</span>
                        {soldOut && <span className="rounded-full bg-red-50 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-crimson no-underline">Sold out</span>}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {variants.length > 0 && (
              <div className="mt-6" data-testid="variant-selector">
                <label htmlFor="variant-select" className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{hasConditions ? "2. Choose color & storage" : "Choose your model (SKU)"}</label>
                <div className="relative mt-3">
                  <select
                    id="variant-select"
                    data-testid="variant-select"
                    value={selected?.id || ""}
                    onChange={(e) => {
                      const v = variants.find((x) => x.id === e.target.value);
                      if (v) handleSelectVariant(v);
                    }}
                    className="w-full appearance-none rounded-xl border border-line bg-white px-4 py-3.5 pr-11 text-sm font-bold text-ink outline-none transition-colors hover:border-ink focus:border-crimson"
                  >
                    {optionVariants.map((v) => (
                      <option key={v.id} value={v.id}>
                        {([v.color, v.storage].filter(Boolean).join(" · ") || "Standard")} — ${v.price}{!v.in_stock ? " · out of stock" : ""}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                </div>
              </div>
            )}

            <div className="mt-8 grid grid-cols-2 gap-4">
              <div className="rounded-2xl border border-line p-5">
                <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Lease from</div>
                <div className="mt-1 font-display text-3xl font-black text-crimson" data-testid="detail-lease-price">${activeWeekly}<span className="text-base font-bold text-muted-foreground">/wk</span></div>
              </div>
              <div className="rounded-2xl border border-line p-5">
                <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Buy outright</div>
                <div className="mt-1 font-display text-3xl font-black text-ink" data-testid="detail-buy-price">${activePrice}</div>
              </div>
            </div>
            <AffirmPromo amount={activePrice} className="mt-3" testId="detail-affirm-promo" />

            <div className="mt-6 flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Qty</span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setQty((q) => Math.max(1, q - 1))}
                    disabled={!activeInStock}
                    data-testid="detail-qty-dec"
                    aria-label="Decrease quantity"
                    className="flex h-9 w-9 items-center justify-center rounded-lg border border-line text-ink transition-colors hover:border-ink disabled:opacity-40"
                  >
                    <Minus className="h-4 w-4" />
                  </button>
                  <span className="w-6 text-center font-bold text-ink" data-testid="detail-qty">{qty}</span>
                  <button
                    onClick={() => setQty((q) => (activeMax != null ? Math.min(activeMax, q + 1) : q + 1))}
                    disabled={!activeInStock || atMax}
                    data-testid="detail-qty-inc"
                    aria-label="Increase quantity"
                    className="flex h-9 w-9 items-center justify-center rounded-lg border border-line text-ink transition-colors hover:border-ink disabled:opacity-40"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
              </div>
              {activeInStock && activeMax != null && activeMax <= 5 && (
                <span className="text-xs font-bold text-crimson" data-testid="detail-stock-left">
                  Only {activeMax} left
                </span>
              )}
            </div>

            <div className="mt-4 flex flex-col gap-3 sm:flex-row">
              <ApplyButton label="Lease with Payvantage" testId="detail-lease-btn" className="justify-center flex-1" />
              <button
                onClick={addToCart}
                disabled={!activeInStock}
                data-testid="detail-add-to-cart"
                className={`inline-flex flex-1 items-center justify-center gap-2 rounded-full px-4 py-4 text-base font-bold transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${activeInStock ? "bg-ink text-white hover:bg-crimson" : "bg-line text-muted-foreground"}`}
              >
                <ShoppingBag className="h-5 w-5" />
                {activeInStock ? "Add to cart" : "Sold out"}
              </button>
            </div>
            {!activeInStock && <p className="mt-3 text-sm font-bold text-crimson" data-testid="detail-oos">Currently out of stock — contact us for availability.</p>}

            <ul className="mt-8 space-y-3 border-t border-line pt-6">
              {specs.map((s) => (
                <li key={s.label} className="flex items-center gap-3 text-sm font-medium text-ink">
                  <s.icon className="h-5 w-5 text-crimson" /> {s.label}
                </li>
              ))}
              {(isIpad(product) || isMacbook(product)) && (
                <li data-testid="detail-charger-included" className="flex items-center gap-3 text-sm font-medium text-ink">
                  <Zap className="h-5 w-5 text-crimson" /> New fast charger + New USB Cable included
                </li>
              )}
              {/iphone/i.test(`${product.name || ""} ${product.model || ""}`) && (
                <li data-testid="detail-iphone-bonus" className="flex items-center gap-3 text-sm font-medium text-ink">
                  <Gift className="h-5 w-5 text-crimson" /> New fast charging kit + Free tempered glass screen protector included
                </li>
              )}
            </ul>
          </div>
        </div>
      </div>

      <div className="mt-24">
        <TrustMarquee />
      </div>
    </div>
  );
}
